import { useMemo, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";

/**
 * Premium custom video player that supports either:
 *  - Direct video URL (mp4, webm) — uses native HTML5 <video> with custom controls
 *  - YouTube URL — parses and embeds as iframe
 * Styled with black + gold BDU aesthetic.
 */
function extractYouTubeId(url = "") {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export default function VideoPlayer({ url, posterUrl, testId = "video-player" }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  const ytId = useMemo(() => extractYouTubeId(url), [url]);

  if (!url) {
    return (
      <div
        className="relative w-full aspect-video bg-bdu-surface border border-bdu-gold/20 flex items-center justify-center"
        data-testid={testId}
      >
        <span className="text-bdu-cream/40 text-sm uppercase tracking-widest">No video yet</span>
      </div>
    );
  }

  // YouTube branch — embedded iframe wrapped in gold frame
  if (ytId) {
    return (
      <div
        className="relative w-full aspect-video border-2 border-bdu-gold/40 bg-black shadow-[0_20px_50px_-15px_rgba(212,175,55,0.4)]"
        data-testid={testId}
      >
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
          title="Founder pitch video"
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // Direct MP4/WebM branch — native player with custom overlay controls
  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };
  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };
  const enterFullscreen = () => {
    if (!videoRef.current) return;
    const el = videoRef.current;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
  };
  const onTime = () => {
    if (!videoRef.current) return;
    const d = videoRef.current.duration || 1;
    setProgress((videoRef.current.currentTime / d) * 100);
  };
  const scrub = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * (videoRef.current.duration || 0);
  };

  return (
    <div
      className="relative w-full aspect-video border-2 border-bdu-gold/40 bg-black shadow-[0_20px_50px_-15px_rgba(212,175,55,0.4)] group overflow-hidden"
      data-testid={testId}
    >
      <video
        ref={videoRef}
        src={url}
        poster={posterUrl || undefined}
        className="w-full h-full object-contain"
        onClick={toggle}
        onTimeUpdate={onTime}
        onEnded={() => setPlaying(false)}
        playsInline
        data-testid={`${testId}-video-el`}
      />

      {/* Big center play button when paused */}
      {!playing && (
        <button
          onClick={toggle}
          data-testid={`${testId}-play-btn`}
          className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px] transition-opacity hover:bg-black/50"
          aria-label="Play video"
        >
          <span className="w-20 h-20 rounded-full bg-bdu-gold/95 text-bdu-black flex items-center justify-center shadow-[0_10px_30px_rgba(212,175,55,0.6)] transition-transform hover:scale-110">
            <Play size={32} fill="currentColor" className="ml-1" />
          </span>
        </button>
      )}

      {/* Bottom control bar */}
      <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div
          onClick={scrub}
          className="w-full h-1 bg-bdu-cream/20 cursor-pointer mb-3 relative"
          data-testid={`${testId}-scrubber`}
        >
          <div
            className="absolute inset-y-0 left-0 bg-bdu-gold"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center gap-3 text-bdu-cream">
          <button onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`${testId}-toggle-btn`} className="hover:text-bdu-gold">
            {playing ? <Pause size={18} /> : <Play size={18} fill="currentColor" />}
          </button>
          <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} data-testid={`${testId}-mute-btn`} className="hover:text-bdu-gold">
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <div className="flex-1" />
          <button onClick={enterFullscreen} aria-label="Fullscreen" data-testid={`${testId}-fs-btn`} className="hover:text-bdu-gold">
            <Maximize2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
